import {
  Component, OnInit, OnDestroy, EventEmitter, Output,
  ViewChild, ViewChildren, QueryList, ElementRef, AfterViewChecked,
  ChangeDetectorRef, NgZone, HostListener
} from '@angular/core';
import { CommonModule, AsyncPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { openDB, IDBPDatabase } from 'idb';

import { AppState } from '@store/app.state';
import { FrameActions } from '@store/actions/frame.actions';
import { selectSettings } from '@store/selectors/settings.selector';
import { selectAllFrames, selectActiveLayerId } from '@store/selectors/frame.selectors';
import { Settings } from '@models/settings.model';
import { PixelFrame, celKey } from '@models/frame.model';
import { generateUUID } from '@app/lib/utility/helper';
import { CollaborationService, PeerState, ChatMessage } from '@app/services/collaboration.service';
import { PxlNumberStepperDirective } from '@app/shared/pxl-number-stepper/pxl-number-stepper.directive';

const IDB_NAME  = 'pxlvd-store';
const IDB_VER   = 1;
const CHAT_STORE = 'chat-messages';

async function openPxlDB(): Promise<IDBPDatabase> {
  return openDB(IDB_NAME, IDB_VER, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(CHAT_STORE)) {
        const s = db.createObjectStore(CHAT_STORE, { keyPath: 'id' });
        s.createIndex('by-timestamp', 'timestamp');
      }
    },
  });
}

@Component({
  selector: 'app-right-side-bar',
  standalone: true,
  imports: [CommonModule, AsyncPipe, FormsModule, DatePipe, PxlNumberStepperDirective],
  templateUrl: './right-side-bar.html',
  styleUrls: ['./right-side-bar.css']
})
export class RightSideBar implements OnInit, OnDestroy, AfterViewChecked {
  @Output() importFileSelected = new EventEmitter<Event>();
  @ViewChild('chatMessagesEl')      private chatMessagesEl?: ElementRef<HTMLDivElement>;
  @ViewChild('videoModalPlayerEl')  private videoModalPlayerEl?: ElementRef<HTMLVideoElement>;
  @ViewChild('cropOverlayEl')       private cropOverlayEl?: ElementRef<HTMLDivElement>;
  @ViewChild('previewStripEl')      private previewStripEl?: ElementRef<HTMLDivElement>;
  @ViewChildren('previewFrameRef')  private previewFrameEls!: QueryList<ElementRef<HTMLDivElement>>;

  settings$: Observable<Settings>;
  allFrames$: Observable<PixelFrame[]>;

  // ── Collab feed ──────────────────────────────────────────────
  isInRoom = false;
  roomId: string | null = null;
  peers: PeerState[] = [];
  statusColor = '#ff6b6b';
  chatEnabled = false;
  chatMutedForMe = false;

  // ── Chat ─────────────────────────────────────────────────────
  chatMessages$ = new BehaviorSubject<ChatMessage[]>([]);
  chatInput = '';
  highlightedMsgId: string | null = null;
  private _highlightTimer: any = null;
  private _shouldScrollChat = false;
  showEmojiPicker = false;
  private _db: IDBPDatabase | null = null;
  get localUsername(): string | null { return this.collabSvc.localUser?.username ?? null; }

  // ── Import tab ────────────────────────────────────────────────
  importTab: 'image' | 'video' = 'image';

  // ── Video import: file / metadata ─────────────────────────────
  videoFile: File | null = null;
  videoObjectUrl: string | null = null;
  videoDuration = 0;
  videoNativeWidth = 0;
  videoNativeHeight = 0;

  // ── Video import: crop / trim ─────────────────────────────────
  videoCropStart = 0;   // timeline start (seconds)
  videoCropEnd   = 0;   // timeline end (seconds)

  // ── Video import: fit mode ────────────────────────────────────
  /** How the video frame maps onto the pixel-art canvas. */
  videoFitMode: 'fill' | 'fit' | 'stretch' | 'custom' = 'custom';
  /** Active crop behavior preset for UI highlighting. */
  videoCropPreset: 'fill' | 'fit' | 'stretch' | 'free' = 'fill';
  /** Currently active ratio preset label (e.g. '16:9'), or '' for none. */
  activeRatioPreset = '';
  /** When true, aspect ratio is locked while dragging crop handles. */
  cropAspectLock = false;
  /** Locked pixel-space aspect ratio W/H (0 = no lock). */
  private _lockedAspect = 0;
  /** Custom crop region - fractions (0–1) of the video's natural dimensions. */
  cropLeft   = 0;
  cropTop    = 0;
  cropRight  = 1;
  cropBottom = 1;

  // ── Crop drag state ───────────────────────────────────────────
  private _dragHandle   = '';
  private _dragStartX   = 0;
  private _dragStartY   = 0;
  private _dragStartCrop = { left: 0, top: 0, right: 1, bottom: 1 };

  // ── Video import: settings ────────────────────────────────────
  videoPixelSize = 4;
  videoFps = 12;
  videoMaxFrames = 0; // 0 = no limit (extract all frames at the given fps)
  videoDithering = false;
  videoDitherStrength = 2;
  videoImportMode: 'append' | 'replace' = 'append';

  // ── Video import: progress / status ──────────────────────────
  videoImporting = false;
  videoImportProgress = 0;
  videoImportStatus = '';
  videoImportStatusTone: 'success' | 'error' | 'info' = 'info';

  // ── Video import modal ────────────────────────────────────────
  showVideoModal = false;

  // ── Detected video FPS ────────────────────────────────────────
  videoNativeFps = 0;
  videoFpsDetecting = false;
  // ── FPS slider drag tooltip ───────────────────────────────────
  videoFpsDragging = false;
  videoFpsTooltipPos = 0; // 0–100 % position along the slider track

  // ── Import video player controls ─────────────────────────────
  importVideoIsPlaying = false;
  importVideoCurrentTime = 0;
  importVideoHasAudio = false;
  importVideoMuted = true;
  importVideoVolume = 1;
  importVideoIsFullscreen = false;

  // ── Frame preview thumbnails ──────────────────────────────────
  videoPreviewFrames: string[] = [];
  previewTimestamps: string[] = [];
  previewTimeValues: number[] = [];
  selectedPreviewIndex = -1;
  previewGenerating = false;
  previewIsPlaying = false;
  private _previewPlayTimer: any = null;
  private _previewDebounceTimer: any = null;
  private _previewGenerationId = 0;

  private readonly _onImportFsChange = () => {
    const doc = document as Document & { webkitFullscreenElement?: Element };
    this.importVideoIsFullscreen = Boolean(doc.fullscreenElement || doc.webkitFullscreenElement);
  };

  /** Bayer 4×4 ordered-dithering threshold matrix (values 0–15). */
  private readonly BAYER_4x4 = [
    [ 0,  8,  2, 10],
    [12,  4, 14,  6],
    [ 3, 11,  1,  9],
    [15,  7, 13,  5],
  ];

  /** Live Settings snapshot, kept in sync via subscription. */
  private _settings: Settings | null = null;

  // ── Computed helpers ──────────────────────────────────────────

  get estimatedFrameCount(): number {
    const end = this.videoCropEnd > 0 ? this.videoCropEnd : this.videoDuration;
    const dur = Math.max(0, end - this.videoCropStart);
    if (!dur || !this.videoFps) return 0;
    const natural = Math.ceil(dur * this.videoFps);
    // videoMaxFrames === 0 means "no limit" - extract all frames
    return this.videoMaxFrames > 0 ? Math.min(this.videoMaxFrames, natural) : natural;
  }

  /** Compute where the video is actually rendered inside the player element
   *  (object-fit: contain leaves black bars). Returns null before metadata loads. */
  get videoRenderInfo(): { left: number; top: number; w: number; h: number } | null {
    const el = this.videoModalPlayerEl?.nativeElement;
    if (!el || !this.videoNativeWidth || !this.videoNativeHeight) return null;
    const elW = el.clientWidth  || el.offsetWidth  || 1;
    const elH = el.clientHeight || el.offsetHeight || 1;
    const vAspect = this.videoNativeWidth / this.videoNativeHeight;
    const eAspect = elW / elH;
    let rW: number, rH: number;
    if (vAspect > eAspect) { rW = elW;            rH = elW / vAspect; }
    else                   { rH = elH;            rW = elH * vAspect; }
    return { left: (elW - rW) / 2, top: (elH - rH) / 2, w: rW, h: rH };
  }

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private collabSvc: CollaborationService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {
    this.settings$    = this.store.select(selectSettings);
    this.allFrames$   = this.store.select(selectAllFrames);
  }

  async ngOnInit(): Promise<void> {
    document.addEventListener('fullscreenchange', this._onImportFsChange);
    document.addEventListener('webkitfullscreenchange', this._onImportFsChange as EventListener);
    this._db = await openPxlDB();

    const allMsgs: ChatMessage[] = await this._db.getAllFromIndex(CHAT_STORE, 'by-timestamp');
    if (allMsgs.length) {
      this.chatMessages$.next(allMsgs);
      this._shouldScrollChat = true;
    }

    this.settings$.pipe(takeUntil(this.destroy$)).subscribe(s => { this._settings = s; });

    this.collabSvc.isInRoom$.pipe(takeUntil(this.destroy$)).subscribe(v => this.isInRoom = v);
    this.collabSvc.roomId$.pipe(takeUntil(this.destroy$)).subscribe(v => this.roomId = v);
    this.collabSvc.peers$.pipe(takeUntil(this.destroy$)).subscribe(v => this.peers = v);
    this.collabSvc.connectionStatus$.pipe(takeUntil(this.destroy$)).subscribe(s => {
      this.statusColor = s === 'connected' ? '#69db7c' : s === 'connecting' ? '#ffd43b' : '#ff6b6b';
    });
    this.collabSvc.collabSettings$.pipe(takeUntil(this.destroy$)).subscribe(s => {
      this.chatEnabled = s.chatEnabled ?? false;
    });
    this.collabSvc.chatMutedUsers$.pipe(takeUntil(this.destroy$)).subscribe(list => {
      const me = this.localUsername;
      this.chatMutedForMe = !!me && list.includes(me);
    });

    this.collabSvc.chatCleared$.pipe(takeUntil(this.destroy$)).subscribe(async () => {
      this.chatMessages$.next([]);
      if (this._db) { try { await this._db.clear(CHAT_STORE); } catch {} }
    });

    this.collabSvc.chatMessage$.pipe(takeUntil(this.destroy$)).subscribe(async msg => {
      if (this._db) {
        await this._db.put(CHAT_STORE, msg);
        const allKeys = await this._db.getAllKeysFromIndex(CHAT_STORE, 'by-timestamp');
        if (allKeys.length > 500) {
          const toDelete = allKeys.slice(0, allKeys.length - 500);
          for (const k of toDelete) { await this._db.delete(CHAT_STORE, k); }
        }
      }
      const current = this.chatMessages$.value;
      if (current.find(m => m.id === msg.id)) return;
      this.chatMessages$.next([...current, msg]);
      this._shouldScrollChat = true;
      this.highlightedMsgId = msg.id;
      clearTimeout(this._highlightTimer);
      this._highlightTimer = setTimeout(() => {
        this.highlightedMsgId = null;
        this.cdr.markForCheck();
      }, 1800);
    });
  }

  ngAfterViewChecked(): void {
    if (this._shouldScrollChat && this.chatMessagesEl) {
      const el = this.chatMessagesEl.nativeElement;
      el.scrollTop = el.scrollHeight;
      this._shouldScrollChat = false;
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this._highlightTimer);
    clearTimeout(this._previewDebounceTimer);
    clearInterval(this._previewPlayTimer);
    document.removeEventListener('fullscreenchange', this._onImportFsChange);
    document.removeEventListener('webkitfullscreenchange', this._onImportFsChange as EventListener);
    if (this.videoObjectUrl) URL.revokeObjectURL(this.videoObjectUrl);
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Chat ──────────────────────────────────────────────────────

  sendChat(): void {
    const text = this.chatInput.trim();
    if (!text || !this.isInRoom || this.chatMutedForMe) return;
    this.collabSvc.sendChatMessage(text);
    this.chatInput = '';
    this.showEmojiPicker = false;
  }

  toggleEmojiPicker(): void { this.showEmojiPicker = !this.showEmojiPicker; }
  insertEmoji(emoji: string): void { this.chatInput += emoji; this.showEmojiPicker = false; }

  readonly EMOJIS = [
    '😀','😂','🥹','😍','🤩','😎','🥳','🤔','😅','😭',
    '❤️','🔥','👍','👎','👏','🙌','🎉','✨','💯','🚀',
    '🎨','🖌️','🖼️','✏️','🎭','🎬','🎮','💻','⚡','🌟',
  ];

  trackById(_: number, msg: ChatMessage): string { return msg.id; }

  // ── Image import ──────────────────────────────────────────────

  importImageTriggered(event: Event): void { this.importFileSelected.emit(event); }

  // ── Video import: file handling ────────────────────────────────

  /** Called when user picks a video file. */
  onVideoFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.clearVideoFile();
    this.videoFile      = file;
    this.videoObjectUrl = URL.createObjectURL(file);
    this.videoImportStatus = '';
    // Load metadata without Angular binding (avoids DomSanitizer loops)
    this._loadVideoMetadata(this.videoObjectUrl);
    // Open modal and assign src to the native <video> element
    this.showVideoModal = true;
    this._applySrcToModalPlayer();
    (event.target as HTMLInputElement).value = '';
  }

  /** Load video metadata using a temporary programmatic element. */
  private _loadVideoMetadata(objectUrl: string): void {
    const tmp = document.createElement('video');
    tmp.muted = true;
    tmp.preload = 'metadata';
    tmp.onloadedmetadata = async () => {
      this.videoDuration     = tmp.duration   || 0;
      this.videoNativeWidth  = tmp.videoWidth  || 0;
      this.videoNativeHeight = tmp.videoHeight || 0;
      this.videoCropStart = 0;
      this.videoCropEnd   = this.videoDuration;
      // Apply default Fill preset so crop region is correctly initialised
      this.applyCropPreset('fill');
      tmp.src = ''; tmp.remove();
      this.cdr.markForCheck();

      // Detect actual FPS of the video
      this.videoFpsDetecting = true;
      this.cdr.markForCheck();
      const detected = await this._detectVideoFps(objectUrl);
      this.videoFpsDetecting = false;
      if (detected > 0) {
        this.videoNativeFps = detected;
        // Auto-set extraction fps to the video's native fps (capped at 60)
        this.videoFps = Math.min(detected, 60);
      }
      this.schedulePreviewRefresh();
      this.cdr.markForCheck();
    };
    tmp.onerror = () => tmp.remove();
    tmp.src = objectUrl;
    tmp.load();
  }

  /** Detect the video's native FPS using requestVideoFrameCallback metadata.mediaTime.
   *
   *  Key design choices:
   *  1. playbackRate = 0.1 - gives the decoder 10× more wall-time per frame so it never
   *     needs to drop frames (the main cause of wrong readings on 4K / high-fps video).
   *  2. mediaTime deltas - the video-stream timestamp difference between consecutive
   *     presented frames equals exactly 1/fps, independent of playback speed or buffering.
   *  3. Median of 8 samples - robust against any remaining outlier (duplicate frame,
   *     stutter at startup, etc.) without requiring more time than the mean would.
   *  4. Snap to common rates - maps the measured raw value to the nearest standard fps
   *     (23.976, 29.97, 59.94, 60, 120 …).
   */
  private _detectVideoFps(objectUrl: string): Promise<number> {
    return new Promise(resolve => {
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      let resolved = false;

      const safeResolve = (fps: number) => {
        if (resolved) return;
        resolved = true;
        clearTimeout(timeoutHandle);
        try { video.pause(); video.src = ''; video.load(); video.remove(); } catch { /* ignore */ }
        resolve(fps);
      };

      // Generous timeout - at 0.1× speed a 24 fps video takes ~3 s for 8 frames
      const timeoutHandle = setTimeout(() => safeResolve(0), 10_000);

      type FrameMeta = { mediaTime: number };
      type RvfcFn = (cb: (now: DOMHighResTimeStamp, meta: FrameMeta) => void) => void;
      const rvfc = ((video as any).requestVideoFrameCallback?.bind(video)) as RvfcFn | undefined;
      if (!rvfc) { clearTimeout(timeoutHandle); safeResolve(0); return; }

      const COMMON_FPS = [8, 10, 12, 15, 20, 23.976, 24, 25, 29.97, 30, 48, 50, 59.94, 60, 90, 120, 144, 240];
      const snapFps = (raw: number): number => {
        let best = Math.round(raw);
        let minDiff = Infinity;
        for (const f of COMMON_FPS) {
          const diff = Math.abs(f - raw);
          if (diff < minDiff) { minDiff = diff; best = Math.round(f); }
        }
        return best;
      };

      let prevMediaTime: number | null = null;
      const durations: number[] = [];

      const onFrame = (_now: DOMHighResTimeStamp, meta: FrameMeta) => {
        if (resolved) return;

        if (prevMediaTime !== null) {
          const d = meta.mediaTime - prevMediaTime;
          // 1/240 fps ≈ 4 ms  … 1/8 fps = 125 ms - anything outside is noise
          if (d > 0.004 && d < 0.125) durations.push(d);
        }
        prevMediaTime = meta.mediaTime;

        if (durations.length >= 8) {
          // Use the MEDIAN so that stray outliers (start-up stutter, a rare duplicate
          // frame) don't skew the result the way a mean would.
          const sorted = [...durations].sort((a, b) => a - b);
          const mid = Math.floor(sorted.length / 2);
          const median = sorted.length % 2 === 0
            ? (sorted[mid - 1] + sorted[mid]) / 2
            : sorted[mid];
          safeResolve(snapFps(1 / median));
        } else {
          rvfc(onFrame);
        }
      };

      video.oncanplay = async () => {
        if (resolved) return;
        // Slow playback = 10× more wall-time per frame → decoder never needs to drop
        // frames even for 4K 60 fps, so every mediaTime delta is exactly 1/fps.
        video.playbackRate = 0.1;
        rvfc(onFrame);
        try { await video.play(); } catch { safeResolve(0); }
      };
      video.onerror = () => safeResolve(0);
      video.src = objectUrl;
      video.load();
    });
  }

  /**
   * Set src on the modal <video> element directly, bypassing Angular property
   * binding. Angular's [src] binding re-evaluates on every change-detection
   * cycle and triggers continuous reload loops with blob: URLs.
   */
  private _applySrcToModalPlayer(): void {
    // setTimeout(0) gives *ngIf a tick to stamp out the DOM before we access it
    setTimeout(() => {
      const v = this.videoModalPlayerEl?.nativeElement;
      if (v && this.videoObjectUrl) {
        v.src = this.videoObjectUrl;
        v.load();
      }
    }, 0);
  }

  /** Clear the chosen video and reset all state. */
  clearVideoFile(): void {
    const v = this.videoModalPlayerEl?.nativeElement;
    if (v) { v.pause(); v.src = ''; v.load(); }
    if (this.videoObjectUrl) { URL.revokeObjectURL(this.videoObjectUrl); this.videoObjectUrl = null; }
    this.videoFile              = null;
    this.videoDuration          = 0;
    this.videoNativeWidth       = 0;
    this.videoNativeHeight      = 0;
    this.videoCropStart         = 0;
    this.videoCropEnd           = 0;
    this.cropLeft = 0; this.cropTop = 0; this.cropRight = 1; this.cropBottom = 1;
    this.videoFitMode           = 'custom';
    this.videoCropPreset        = 'fill';
    this.activeRatioPreset      = '';
    this.cropAspectLock         = false;
    this._lockedAspect          = 0;
    this.videoImportStatus      = '';
    this.videoImportProgress    = 0;
    this.videoPreviewFrames     = [];
    this.previewTimestamps      = [];
    this.previewTimeValues      = [];
    this.selectedPreviewIndex   = -1;
    this.videoNativeFps         = 0;
    this.videoFpsDetecting      = false;
    this.videoFpsDragging       = false;
    this.importVideoIsPlaying   = false;
    this.importVideoCurrentTime = 0;
    this.importVideoHasAudio    = false;
    this.importVideoIsFullscreen = false;
    this._pausePreviewPlay();
    this.showVideoModal         = false;
    clearTimeout(this._previewDebounceTimer);
  }

  openVideoModal(): void {
    this.showVideoModal = true;
    this._applySrcToModalPlayer();
  }

  closeVideoModal(): void { this.showVideoModal = false; }

  onVideoModalBackdropClick(e: MouseEvent): void {
    if ((e.target as HTMLElement).classList.contains('video-modal-backdrop')) {
      this.closeVideoModal();
    }
  }

  /** Format seconds as m:ss. */
  formatDuration(s: number): string {
    if (!Number.isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const sc = Math.floor(s % 60);
    return `${m}:${String(sc).padStart(2, '0')}`;
  }

  // ── Fit mode & crop ───────────────────────────────────────────

  /**
   * Apply a crop preset: fill, fit, stretch, or free (free-form custom).
   * Fill / Fit / Stretch each set a canonical crop region that mirrors
   * the old drawing-mode semantics; free mode keeps the existing crop.
   */
  applyCropPreset(preset: 'fill' | 'fit' | 'stretch' | 'free'): void {
    this.videoCropPreset = preset;
    this.activeRatioPreset = '';

    switch (preset) {
      case 'fill': {
        // Center-crop video to match canvas aspect - no black bars
        this.videoFitMode = 'fill';
        this.cropAspectLock = false;
        this._lockedAspect = 0;
        this._computeFillCrop();
        break;
      }
      case 'fit': {
        // Show full video letterboxed - crop region = full frame
        this.videoFitMode = 'fit';
        this.cropAspectLock = false;
        this._lockedAspect = 0;
        this.cropLeft = 0; this.cropTop = 0; this.cropRight = 1; this.cropBottom = 1;
        break;
      }
      case 'stretch': {
        // Stretch full frame to canvas - no bars, may distort
        this.videoFitMode = 'custom';
        this.cropAspectLock = false;
        this._lockedAspect = 0;
        this.cropLeft = 0; this.cropTop = 0; this.cropRight = 1; this.cropBottom = 1;
        break;
      }
      case 'free': {
        // Free-form drag crop - keep current crop values
        this.videoFitMode = 'custom';
        this.cropAspectLock = false;
        this._lockedAspect = 0;
        break;
      }
    }
    this.schedulePreviewRefresh();
  }

  /** @deprecated kept for backward-compat; routes through applyCropPreset or free mode */
  setFitMode(mode: 'fill' | 'fit' | 'stretch' | 'custom'): void {
    if (mode === 'custom') {
      this.applyCropPreset('free');
    } else {
      this.applyCropPreset(mode as 'fill' | 'fit' | 'stretch');
    }
  }

  /** Compute and store the fill crop region (center-crop to match canvas aspect ratio). */
  private _computeFillCrop(): void {
    if (!this.videoNativeWidth || !this.videoNativeHeight || !this._settings) {
      this.cropLeft = 0; this.cropTop = 0; this.cropRight = 1; this.cropBottom = 1;
      return;
    }
    const vW = this.videoNativeWidth;
    const vH = this.videoNativeHeight;
    const dW = this._settings.Resolution.width;
    const dH = this._settings.Resolution.height;
    const vAspect = vW / vH;
    const tAspect = dW / dH;
    if (Math.abs(vAspect - tAspect) < 0.01) {
      this.cropLeft = 0; this.cropTop = 0; this.cropRight = 1; this.cropBottom = 1;
    } else if (vAspect > tAspect) {
      const cW = tAspect / vAspect;
      this.cropLeft = (1 - cW) / 2; this.cropRight  = (1 + cW) / 2;
      this.cropTop  = 0;            this.cropBottom  = 1;
    } else {
      const cH = vAspect / tAspect;
      this.cropTop   = (1 - cH) / 2; this.cropBottom = (1 + cH) / 2;
      this.cropLeft  = 0;            this.cropRight   = 1;
    }
  }

  /** Apply a standard aspect-ratio preset to the crop region and lock ratio. */
  setCropRatio(wRatio: number, hRatio: number): void {
    this.videoFitMode = 'custom';
    this.videoCropPreset = 'free';
    this.activeRatioPreset = `${wRatio}:${hRatio}`;
    this._lockedAspect = wRatio / hRatio;
    this.cropAspectLock = true;
    const vW = this.videoNativeWidth || 1;
    const vH = this.videoNativeHeight || 1;
    const vAspect = vW / vH;
    const tAspect = wRatio / hRatio;
    if (tAspect > vAspect) {
      const cH = vAspect / tAspect;
      this.cropLeft = 0; this.cropRight  = 1;
      this.cropTop  = (1 - cH) / 2; this.cropBottom = (1 + cH) / 2;
    } else {
      const cW = tAspect / vAspect;
      this.cropTop  = 0; this.cropBottom = 1;
      this.cropLeft = (1 - cW) / 2; this.cropRight = (1 + cW) / 2;
    }
    this.schedulePreviewRefresh();
  }

  resetCrop(): void {
    this.videoCropPreset = 'free';
    this.activeRatioPreset = '';
    this.cropAspectLock = false;
    this._lockedAspect = 0;
    this.videoFitMode = 'custom';
    this.cropLeft = 0; this.cropTop = 0; this.cropRight = 1; this.cropBottom = 1;
    this.schedulePreviewRefresh();
  }

  // ── Crop drag (mouse events via @HostListener) ─────────────────

  onCropHandleDown(e: MouseEvent, handle: string): void {
    e.preventDefault(); e.stopPropagation();
    // Switch to free/custom mode when user drags handles
    if (handle !== 'move') {
      this.videoFitMode = 'custom';
      this.videoCropPreset = 'free';
    }
    this._dragHandle   = handle;
    this._dragStartX   = e.clientX;
    this._dragStartY   = e.clientY;
    this._dragStartCrop = {
      left: this.cropLeft, top: this.cropTop,
      right: this.cropRight, bottom: this.cropBottom
    };
  }

  @HostListener('document:mousemove', ['$event'])
  onDocMouseMove(e: MouseEvent): void {
    if (!this._dragHandle) return;
    const el = this.cropOverlayEl?.nativeElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const dx = (e.clientX - this._dragStartX) / rect.width;
    const dy = (e.clientY - this._dragStartY) / rect.height;
    const MIN = 0.05;
    let { left, top, right, bottom } = this._dragStartCrop;

    switch (this._dragHandle) {
      case 'tl': left  = clamp(left  + dx, 0, right  - MIN);
                 top   = clamp(top   + dy, 0, bottom - MIN); break;
      case 'tr': right  = clamp(right + dx, left + MIN, 1);
                 top    = clamp(top   + dy, 0, bottom - MIN); break;
      case 'bl': left   = clamp(left  + dx, 0, right - MIN);
                 bottom = clamp(bottom + dy, top + MIN, 1); break;
      case 'br': right  = clamp(right + dx, left + MIN, 1);
                 bottom = clamp(bottom + dy, top + MIN, 1); break;
      case 't':  top    = clamp(top   + dy, 0, bottom - MIN); break;
      case 'b':  bottom = clamp(bottom + dy, top + MIN, 1); break;
      case 'l':  left   = clamp(left  + dx, 0, right - MIN); break;
      case 'r':  right  = clamp(right + dx, left + MIN, 1); break;
      case 'move': {
        const w = right - left, h = bottom - top;
        left   = clamp(left   + dx, 0, 1 - w);
        top    = clamp(top    + dy, 0, 1 - h);
        right  = left + w;
        bottom = top  + h;
        break;
      }
    }

    // ── Aspect-ratio lock enforcement ────────────────────────────
    if (this.cropAspectLock && this._lockedAspect > 0 && this._dragHandle !== 'move') {
      const vW = this.videoNativeWidth || 1;
      const vH = this.videoNativeHeight || 1;
      // Fractional aspect: (right-left)/(bottom-top) = _lockedAspect * (vH/vW)
      const fa = this._lockedAspect * (vH / vW);
      const h = this._dragHandle;
      if (h === 't' || h === 'b') {
        // Height driven - adjust width from height, keep horizontal center
        const newCW = (bottom - top) * fa;
        const cx = (this._dragStartCrop.left + this._dragStartCrop.right) / 2;
        left  = clamp(cx - newCW / 2, 0, 1 - MIN);
        right = clamp(cx + newCW / 2, MIN, 1);
      } else {
        // Width driven - adjust height from width
        const newCH = (right - left) / fa;
        if (h === 'tl' || h === 'tr') {
          top = clamp(bottom - newCH, 0, bottom - MIN);
        } else {
          bottom = clamp(top + newCH, top + MIN, 1);
        }
      }
    }

    this.cropLeft = left; this.cropTop = top;
    this.cropRight = right; this.cropBottom = bottom;
    this.schedulePreviewRefresh();
  }

  /** Seek the modal video player to the clicked preview frame's timestamp. */
  seekVideoPreviewFrame(index: number): void {
    const t = this.previewTimeValues[index];
    const v = this.videoModalPlayerEl?.nativeElement;
    if (!v || !Number.isFinite(t)) return;
    this.selectedPreviewIndex = index;
    v.currentTime = Math.max(0, t);
  }

  // ── Frame preview generation ──────────────────────────────────

  schedulePreviewRefresh(): void {
    clearTimeout(this._previewDebounceTimer);
    this._previewDebounceTimer = setTimeout(() => { void this.generatePreviewFrames(); }, 450);
  }

  async generatePreviewFrames(): Promise<void> {
    if (!this.videoFile || !this._settings) return;
    const myId = ++this._previewGenerationId;
    this.previewGenerating = true;
    this.videoPreviewFrames = [];
    this.previewTimestamps  = [];
    this.previewTimeValues  = [];
    this.selectedPreviewIndex = -1;

    // Stop any running preview animation before regenerating
    this._pausePreviewPlay();

    const { width, height } = this._settings.Resolution;
    // Show all estimated frames (up to 48 thumbnails for perf)
    const PREVIEW_COUNT = Math.max(5, Math.min(this.estimatedFrameCount, 48));
    const THUMB_MAX = 64;
    const thumbScale = THUMB_MAX / Math.max(width, height, 1);
    const thumbW = Math.max(1, Math.round(width  * thumbScale));
    const thumbH = Math.max(1, Math.round(height * thumbScale));

    const cropStart = this.videoCropStart;
    const cropEnd   = Math.max(cropStart + 0.01, this.videoCropEnd || this.videoDuration);
    const cropDur   = cropEnd - cropStart;

    try {
      const video = document.createElement('video');
      video.muted = true; video.playsInline = true;
      video.preload = 'auto'; video.crossOrigin = 'anonymous';
      const objectUrl = URL.createObjectURL(this.videoFile);

      await new Promise<void>((res, rej) => {
        video.onloadedmetadata = () => res();
        video.onerror = () => rej(new Error('Video load error'));
        video.src = objectUrl; video.load();
      });

      if (myId !== this._previewGenerationId) { URL.revokeObjectURL(objectUrl); video.remove(); return; }

      const canvas = document.createElement('canvas');
      canvas.width = thumbW; canvas.height = thumbH;
      const ctx = canvas.getContext('2d')!;
      ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';

      const frames: string[] = [];
      const times:  string[] = [];
      const timeVals: number[] = [];

      for (let i = 0; i < PREVIEW_COUNT; i++) {
        if (myId !== this._previewGenerationId) break;
        const t    = cropStart + cropDur * (i / (PREVIEW_COUNT - 1 || 1));
        const time = Math.min(Math.max(0, t), video.duration - 0.001);
        await this._seekVideo(video, time);
        this._drawFrameWithFitMode(ctx, video, thumbW, thumbH);
        const imgData = ctx.getImageData(0, 0, thumbW, thumbH);
        const scaledPixel = Math.max(1, Math.round(this.videoPixelSize * thumbScale));
        this._drawPixelatedPreview(ctx, imgData, thumbW, thumbH, scaledPixel, this.videoDithering, this.videoDitherStrength);
        frames.push(canvas.toDataURL('image/png'));
        times.push(this.formatDuration(t));
        timeVals.push(Math.max(0, t));
      }

      URL.revokeObjectURL(objectUrl); video.remove();

      if (myId === this._previewGenerationId) {
        this.videoPreviewFrames = frames;
        this.previewTimestamps  = times;
        this.previewTimeValues  = timeVals;
      }
    } catch (err) {
      console.warn('[video-preview]', err);
    } finally {
      if (myId === this._previewGenerationId) {
        this.previewGenerating = false;
        this.cdr.markForCheck();
      }
    }
  }

  // ── Video frame extraction ────────────────────────────────────

  async processVideoImport(): Promise<void> {
    if (!this.videoFile || this.videoImporting) return;
    const settings = this._settings;
    if (!settings) { this._setVideoStatus('error', 'No project settings found.'); return; }

    this.videoImporting      = true;
    this.videoImportProgress = 0;
    this.videoImportStatus   = '';

    // Snapshot the current active layer ID synchronously before async work.
    let activeLayerId: string | null = null;
    this.store.select(selectActiveLayerId).pipe(take(1)).subscribe(id => { activeLayerId = id; });

    try {
      const { frames, cels } = await this._extractFramesFromVideo(settings, activeLayerId);
      if (!frames.length) { this._setVideoStatus('error', 'No frames extracted. Check video format.'); return; }
      if (this.videoImportMode === 'replace') {
        this.store.dispatch(FrameActions.setFrames({ frames, cels }));
      } else {
        this.store.dispatch(FrameActions.appendFrames({ frames, cels }));
      }
      this._setVideoStatus('success', `✓ ${frames.length} pixel frames imported!`);
      setTimeout(() => { this.closeVideoModal(); }, 1200);
    } catch (err) {
      console.error('[video-import]', err);
      this._setVideoStatus('error', 'Video processing failed. Try a different format.');
    } finally {
      this.videoImporting = false;
    }
  }

  private _setVideoStatus(tone: 'success' | 'error' | 'info', msg: string): void {
    this.videoImportStatusTone = tone;
    this.videoImportStatus     = msg;
  }

  private _extractFramesFromVideo(
    settings: Settings,
    activeLayerId: string | null
  ): Promise<{ frames: PixelFrame[]; cels: Record<string, string[][]> }> {
    return new Promise((resolve, reject) => {
      if (!this.videoFile) { reject(new Error('No file')); return; }
      const { width, height } = settings.Resolution;
      const pixelSize      = Math.max(1, this.videoPixelSize);
      const fps            = Math.max(1, this.videoFps);
      // 0 = no limit; otherwise respect the user-set cap
      const maxFrames      = this.videoMaxFrames > 0 ? this.videoMaxFrames : Infinity;
      const dithering      = this.videoDithering;
      const ditherStrength = this.videoDitherStrength;
      const cropStart      = this.videoCropStart;

      const video = document.createElement('video');
      video.muted = true; video.playsInline = true;
      video.preload = 'auto'; video.crossOrigin = 'anonymous';
      const objectUrl = URL.createObjectURL(this.videoFile);

      video.onloadedmetadata = async () => {
        const fullDuration = video.duration;
        if (!Number.isFinite(fullDuration) || fullDuration <= 0) {
          URL.revokeObjectURL(objectUrl); reject(new Error('Invalid video duration')); return;
        }
        const cropEnd     = Math.max(cropStart + 0.001, this.videoCropEnd || fullDuration);
        const cropDur     = Math.min(cropEnd, fullDuration) - cropStart;
        const totalFrames = Math.min(maxFrames, Math.ceil(cropDur * fps));
        const frames: PixelFrame[] = [];
        const cels: Record<string, string[][]> = {};

        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';

        try {
          for (let i = 0; i < totalFrames; i++) {
            const time = Math.min(cropStart + i / fps, cropEnd - 0.001);
            await this._seekVideo(video, Math.max(0, time));

            // Draw frame respecting the chosen fit mode / custom crop
            this._drawFrameWithFitMode(ctx, video, width, height);

            const imageData = ctx.getImageData(0, 0, width, height);
            const pixelGrid = this._pixelateImageData(imageData, width, height, pixelSize, dithering, ditherStrength);

            const frameId = generateUUID();
            frames.push({ id: frameId, pixels: pixelGrid });
            // Attach pixels as a cel on the active layer (if any)
            if (activeLayerId) {
              cels[celKey(frameId, activeLayerId)] = pixelGrid;
            }
            this.videoImportProgress = Math.round(((i + 1) / totalFrames) * 100);
          }
          URL.revokeObjectURL(objectUrl); video.remove();
          resolve({ frames, cels });
        } catch (err) {
          URL.revokeObjectURL(objectUrl); video.remove(); reject(err);
        }
      };

      video.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Video load error')); };
      video.src = objectUrl; video.load();
    });
  }

  // ── Drawing helpers ───────────────────────────────────────────

  /**
   * Draw one video frame onto ctx using the current fit mode.
   * Works for any canvas size (used for both preview thumbnails and extraction).
   */
  private _drawFrameWithFitMode(
    ctx: CanvasRenderingContext2D,
    video: HTMLVideoElement,
    destW: number, destH: number
  ): void {
    const vW = video.videoWidth  || destW;
    const vH = video.videoHeight || destH;
    const tAspect = destW / destH;
    const vAspect = vW / vH;
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, destW, destH);

    switch (this.videoFitMode) {
      case 'stretch':
        // Full frame stretched to canvas (crop is 0,0,1,1 in this mode)
        ctx.drawImage(video, 0, 0, destW, destH);
        break;

      case 'fit': {
        // Letterbox / pillarbox - no cropping, full video frame, black bars
        let rW: number, rH: number;
        if (vAspect > tAspect) { rW = destW;       rH = destW / vAspect; }
        else                   { rH = destH;       rW = destH * vAspect; }
        ctx.drawImage(video, (destW - rW) / 2, (destH - rH) / 2, rW, rH);
        break;
      }

      // 'fill' stores its crop in cropLeft/etc (via _computeFillCrop), so fall through to 'custom'.
      case 'fill':
      case 'custom':
      default: {
        // Use stored crop region (works for fill, free-crop, and stretch-as-custom)
        const srcX = this.cropLeft   * vW;
        const srcY = this.cropTop    * vH;
        const srcW = (this.cropRight  - this.cropLeft)   * vW;
        const srcH = (this.cropBottom - this.cropTop)    * vH;
        if (srcW > 0 && srcH > 0) ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, destW, destH);
        break;
      }
    }
  }

  /** Draw pixelated preview onto ctx using block-averaged filled rectangles. */
  private _drawPixelatedPreview(
    ctx: CanvasRenderingContext2D, imageData: ImageData,
    width: number, height: number,
    pixelSize: number, dithering: boolean, ditherStrength: number
  ): void {
    const blockCols = Math.ceil(width  / pixelSize);
    const blockRows = Math.ceil(height / pixelSize);
    for (let br = 0; br < blockRows; br++) {
      for (let bc = 0; bc < blockCols; bc++) {
        const sR = br * pixelSize, sC = bc * pixelSize;
        const eR = Math.min(sR + pixelSize, height);
        const eC = Math.min(sC + pixelSize, width);
        let rS = 0, gS = 0, bS = 0, n = 0;
        for (let gr = sR; gr < eR; gr++) {
          for (let gc = sC; gc < eC; gc++) {
            const idx = (gr * width + gc) * 4;
            rS += imageData.data[idx]; gS += imageData.data[idx+1]; bS += imageData.data[idx+2]; n++;
          }
        }
        let r = Math.round(rS / n), g = Math.round(gS / n), b = Math.round(bS / n);
        if (dithering) {
          const bayer = this.BAYER_4x4[(br % 4 + 4) % 4][(bc % 4 + 4) % 4];
          const thr = (bayer / 15 - 0.5) * (64 * ditherStrength);
          r = clamp(r + thr, 0, 255); g = clamp(g + thr, 0, 255); b = clamp(b + thr, 0, 255);
        }
        ctx.fillStyle = `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
        ctx.fillRect(sC, sR, eC - sC, eR - sR);
      }
    }
  }

  /** Full-resolution pixelation returning a 2-D hex array. */
  private _pixelateImageData(
    imageData: ImageData, width: number, height: number,
    pixelSize: number, dithering: boolean, ditherStrength: number
  ): string[][] {
    const pixels: string[][] = Array.from({ length: height }, () => Array(width).fill('#000000'));
    const bCols = Math.ceil(width / pixelSize);
    const bRows = Math.ceil(height / pixelSize);
    for (let br = 0; br < bRows; br++) {
      for (let bc = 0; bc < bCols; bc++) {
        const sR = br * pixelSize, sC = bc * pixelSize;
        const eR = Math.min(sR + pixelSize, height);
        const eC = Math.min(sC + pixelSize, width);
        let rS = 0, gS = 0, bS = 0, n = 0;
        for (let gr = sR; gr < eR; gr++) {
          for (let gc = sC; gc < eC; gc++) {
            const idx = (gr * width + gc) * 4;
            rS += imageData.data[idx]; gS += imageData.data[idx+1]; bS += imageData.data[idx+2]; n++;
          }
        }
        const rA = Math.round(rS / n), gA = Math.round(gS / n), bA = Math.round(bS / n);
        for (let gr = sR; gr < eR; gr++) {
          for (let gc = sC; gc < eC; gc++) {
            let r = rA, g = gA, b = bA;
            if (dithering) {
              const bayer = this.BAYER_4x4[(gr % 4 + 4) % 4][(gc % 4 + 4) % 4];
              const thr = (bayer / 15 - 0.5) * (64 * ditherStrength);
              r = clamp(r + thr, 0, 255); g = clamp(g + thr, 0, 255); b = clamp(b + thr, 0, 255);
            }
            pixels[gr][gc] = '#'
              + Math.round(r).toString(16).padStart(2, '0')
              + Math.round(g).toString(16).padStart(2, '0')
              + Math.round(b).toString(16).padStart(2, '0');
          }
        }
      }
    }
    return pixels;
  }

  private _seekVideo(video: HTMLVideoElement, time: number): Promise<void> {
    return new Promise(resolve => {
      if (Math.abs(video.currentTime - time) < 0.001) { resolve(); return; }
      const onSeeked = () => { video.removeEventListener('seeked', onSeeked); resolve(); };
      video.addEventListener('seeked', onSeeked);
      video.currentTime = time;
    });
  }

  // ── FPS slider drag tooltip ───────────────────────────────────

  onFpsSliderInput(e: Event): void {
    const slider = e.target as HTMLInputElement;
    const val = +slider.value;
    this.videoFps = val;
    const min = +(slider.min || '1');
    const max = +(slider.max || '120');
    this.videoFpsTooltipPos = ((val - min) / (max - min)) * 100;
    this.schedulePreviewRefresh();
  }

  @HostListener('document:mouseup')
  onDocMouseUp(): void {
    this._dragHandle = '';
    this.videoFpsDragging = false;
  }

  @HostListener('document:touchend')
  onDocTouchEnd(): void { this.videoFpsDragging = false; }

  // ── Import video player controls ──────────────────────────────

  onImportVideoLoadedMetadata(v: HTMLVideoElement): void {
    this.importVideoCurrentTime = 0;
    this._updateImportAudioAvailability(v);
  }

  onImportVideoTimeUpdate(v: HTMLVideoElement): void {
    this.importVideoCurrentTime = v.currentTime;
    if (!this.importVideoHasAudio) this._updateImportAudioAvailability(v);
  }

  onImportVideoPlayState(playing: boolean): void {
    this.importVideoIsPlaying = playing;
  }

  async toggleImportPlayback(v: HTMLVideoElement): Promise<void> {
    if (v.paused) {
      try { await v.play(); } catch { this.importVideoIsPlaying = false; }
    } else {
      v.pause();
    }
  }

  seekImportVideo(val: string, v: HTMLVideoElement): void {
    const t = Number(val);
    if (!Number.isFinite(t)) return;
    v.currentTime = Math.max(0, Math.min(this.videoDuration || 0, t));
    this.importVideoCurrentTime = v.currentTime;
  }

  toggleImportMuted(v: HTMLVideoElement): void {
    if (!this.importVideoHasAudio) return;
    this.importVideoMuted = !this.importVideoMuted;
    v.muted = this.importVideoMuted;
  }

  setImportVolume(val: string, v: HTMLVideoElement): void {
    if (!this.importVideoHasAudio) return;
    const vol = Math.max(0, Math.min(1, Number(val)));
    if (!Number.isFinite(vol)) return;
    this.importVideoVolume = vol;
    v.volume = vol;
    this.importVideoMuted = vol === 0;
    v.muted = this.importVideoMuted;
  }

  async toggleImportFullscreen(v: HTMLVideoElement): Promise<void> {
    const doc = document as Document & {
      webkitFullscreenElement?: Element;
      webkitExitFullscreen?: () => Promise<void> | void;
    };
    if (doc.fullscreenElement || doc.webkitFullscreenElement) {
      if (document.exitFullscreen) await document.exitFullscreen();
      else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen();
      this.importVideoIsFullscreen = false;
      return;
    }
    const fsV = v as HTMLVideoElement & { webkitRequestFullscreen?: () => Promise<void> | void };
    if (v.requestFullscreen) { await v.requestFullscreen(); this.importVideoIsFullscreen = true; }
    else if (fsV.webkitRequestFullscreen) { await fsV.webkitRequestFullscreen(); this.importVideoIsFullscreen = true; }
  }

  private _updateImportAudioAvailability(v: HTMLVideoElement): void {
    const candidate = v as HTMLVideoElement & {
      audioTracks?: { length: number };
      mozHasAudio?: boolean;
      webkitAudioDecodedByteCount?: number;
      captureStream?: () => MediaStream;
    };
    const fromAudioTracks = (candidate.audioTracks?.length ?? 0) > 0;
    const fromMoz = candidate.mozHasAudio === true;
    const fromWebkit = (candidate.webkitAudioDecodedByteCount ?? 0) > 0;
    let fromCapture = false;
    if (!fromAudioTracks && !fromMoz && !fromWebkit && candidate.captureStream) {
      try {
        const s = candidate.captureStream();
        fromCapture = s.getAudioTracks().length > 0;
        s.getTracks().forEach(t => t.stop());
      } catch { /* ignore */ }
    }
    this.importVideoHasAudio = fromAudioTracks || fromMoz || fromWebkit || fromCapture;
    if (!this.importVideoHasAudio) { this.importVideoMuted = true; v.muted = true; }
  }

  // ── Preview strip playback ────────────────────────────────────

  togglePreviewPlay(): void {
    if (this.previewIsPlaying) { this._pausePreviewPlay(); } else { this._startPreviewPlay(); }
  }

  private _startPreviewPlay(): void {
    if (!this.videoPreviewFrames.length) return;
    this.previewIsPlaying = true;
    if (this.selectedPreviewIndex < 0) this.selectedPreviewIndex = 0;
    // Cap preview animation fps at 60fps
    const fps = Math.min(Math.max(1, this.videoFps), 60);
    const interval = 1000 / fps;
    this._previewPlayTimer = setInterval(() => {
      // Wrap inside zone.run so Angular CD reliably picks up the change
      this.zone.run(() => {
        this.selectedPreviewIndex = (this.selectedPreviewIndex + 1) % this.videoPreviewFrames.length;
        // Seek modal video to matching timestamp so the player stays in sync
        const t = this.previewTimeValues[this.selectedPreviewIndex];
        const v = this.videoModalPlayerEl?.nativeElement;
        if (v && Number.isFinite(t)) v.currentTime = t;
        this.cdr.markForCheck();
        // Scroll the active thumbnail into view within the strip
        const frameEl = this.previewFrameEls?.get(this.selectedPreviewIndex)?.nativeElement;
        frameEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      });
    }, interval);
  }

  private _pausePreviewPlay(): void {
    this.previewIsPlaying = false;
    clearInterval(this._previewPlayTimer);
    this._previewPlayTimer = null;
  }

}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
